// Antar Surat report generator: PDF (jsPDF + autoTable) and Word (docx).
// Includes column for photo (foto bukti) with adjustable size in mm.
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import {
  Document, Packer, Paragraph, Table, TableRow, TableCell,
  HeadingLevel, AlignmentType, TextRun, ImageRun, WidthType, BorderStyle,
  PageBreak, VerticalAlign,
} from "docx";

const STATUS_LABEL = {
  pending: "Belum",
  delivered: "Sudah",
  not_found: "Tidak Ketemu",
  promised_pay: "Janji Bayar",
  lunas: "Lunas",
  putus: "Putus",
};

const fmtStatus = (it) => {
  const s = it.status || "pending";
  const lbl = STATUS_LABEL[s] || "Belum";
  if (s === "promised_pay" && it.promised_date) return `${lbl} (${it.promised_date})`;
  return lbl;
};

const slugify = (s) => String(s || "antar-surat").replace(/[^\w-]+/g, "_");

// Convert a data:image/... URL into an HTMLImageElement (await onload).
const loadImage = (dataUrl) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = dataUrl;
  });

// Resize an image dataURL to fit inside maxWmm/maxHmm at ~150 DPI, returns
// { dataUrl, width, height } in mm preserving aspect ratio.
const resizeForPdf = async (dataUrl, maxWmm, maxHmm) => {
  try {
    const img = await loadImage(dataUrl);
    const ratio = img.width / img.height;
    let wmm = maxWmm;
    let hmm = wmm / ratio;
    if (hmm > maxHmm) { hmm = maxHmm; wmm = hmm * ratio; }
    // Render to canvas at ~150 DPI (5.9 px per mm) for crisp PDF.
    const dpi = 150;
    const pxPerMm = dpi / 25.4;
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(wmm * pxPerMm));
    canvas.height = Math.max(1, Math.round(hmm * pxPerMm));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/jpeg", 0.85), width: wmm, height: hmm };
  } catch {
    return null;
  }
};

// Resize an image dataURL to a specific pixel width/height for docx (keeps aspect).
const resizeForDocx = async (dataUrl, maxWpx, maxHpx) => {
  try {
    const img = await loadImage(dataUrl);
    const ratio = img.width / img.height;
    let w = maxWpx;
    let h = w / ratio;
    if (h > maxHpx) { h = maxHpx; w = h * ratio; }
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(w));
    canvas.height = Math.max(1, Math.round(h));
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const out = canvas.toDataURL("image/jpeg", 0.85);
    // Convert base64 to Uint8Array for docx
    const b64 = out.split(",")[1];
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { bytes, width: Math.round(w), height: Math.round(h) };
  } catch {
    return null;
  }
};

// =============== PDF EXPORT ===============
// photoSize: "small" | "medium" | "large" | "none"  → controls photo column size in mm
// statuses: optional array of status keys to include (e.g. ["putus","lunas"]). null/empty = all.
export const exportDeliveryPdf = async (list, photoSize = "medium", statuses = null) => {
  const allItems = list?.items || [];
  const items = (Array.isArray(statuses) && statuses.length > 0)
    ? allItems.filter((it) => statuses.includes(it.status || "pending"))
    : allItems;
  const name = list?.name || "Antar Surat";
  const updated = list?.updated_at
    ? new Date(list.updated_at).toLocaleString("id-ID")
    : new Date().toLocaleString("id-ID");

  // Map photoSize → mm cell size
  const SIZE_MM = { none: 0, small: 18, medium: 30, large: 45 };
  const photoMm = SIZE_MM[photoSize] ?? 30;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(name, 14, 16);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.text(`Total ${items.length} ID · diperbarui ${updated}`, 14, 22);

  // Counts summary
  const counts = items.reduce((acc, it) => {
    const s = it.status || "pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const summary = [
    `Belum: ${counts.pending || 0}`,
    `Sudah: ${counts.delivered || 0}`,
    `Tidak Ketemu: ${counts.not_found || 0}`,
    `Janji Bayar: ${counts.promised_pay || 0}`,
  ].join("   ·   ");
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text(summary, 14, 27);
  doc.setTextColor(0);

  // Pre-resize photos so autoTable can place them with consistent size.
  const photoCache = new Map();
  if (photoMm > 0) {
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.photo && typeof it.photo === "string" && it.photo.startsWith("data:image/")) {
        const r = await resizeForPdf(it.photo, photoMm, photoMm);
        if (r) photoCache.set(i, r);
      }
    }
  }

  const head = photoMm > 0
    ? [["No", "ID Pelanggan", "Nama Pelanggan", "Catatan", "Status", "Foto Bukti"]]
    : [["No", "ID Pelanggan", "Nama Pelanggan", "Catatan", "Status"]];

  const body = items.map((it, i) => {
    const row = [
      String(i + 1),
      it.customer_id || "",
      it.nama || "-",
      it.note || "",
      fmtStatus(it),
    ];
    if (photoMm > 0) row.push(""); // placeholder for image
    return row;
  });

  autoTable(doc, {
    head,
    body,
    startY: 32,
    margin: { left: 10, right: 10 },
    styles: { fontSize: 9, cellPadding: 2, valign: "middle", overflow: "linebreak" },
    headStyles: { fillColor: [16, 128, 100], textColor: 255, fontStyle: "bold" },
    columnStyles: photoMm > 0
      ? {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 28, font: "courier" },
          2: { cellWidth: 38 },
          3: { cellWidth: 40 },
          4: { cellWidth: 25, halign: "center" },
          5: { cellWidth: photoMm + 4, halign: "center" },
        }
      : {
          0: { cellWidth: 10, halign: "center" },
          1: { cellWidth: 32, font: "courier" },
          2: { cellWidth: 55 },
          3: { cellWidth: 60 },
          4: { cellWidth: 30, halign: "center" },
        },
    didParseCell: (data) => {
      if (photoMm > 0 && data.section === "body" && data.column.index === 5) {
        data.cell.styles.minCellHeight = photoMm + 2;
      }
    },
    didDrawCell: (data) => {
      if (photoMm > 0 && data.section === "body" && data.column.index === 5) {
        const cached = photoCache.get(data.row.index);
        if (cached) {
          const cx = data.cell.x + (data.cell.width - cached.width) / 2;
          const cy = data.cell.y + (data.cell.height - cached.height) / 2;
          try {
            doc.addImage(cached.dataUrl, "JPEG", cx, cy, cached.width, cached.height);
          } catch {/* ignore */}
        }
      }
    },
  });

  // Footer with page numbers
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Halaman ${p} / ${pageCount}`,
      doc.internal.pageSize.getWidth() - 10,
      doc.internal.pageSize.getHeight() - 6,
      { align: "right" }
    );
  }

  doc.save(`${slugify(name)}.pdf`);
};

// =============== WORD (.docx) EXPORT ===============
export const exportDeliveryDocx = async (list, photoSize = "medium", statuses = null) => {
  const allItems = list?.items || [];
  const items = (Array.isArray(statuses) && statuses.length > 0)
    ? allItems.filter((it) => statuses.includes(it.status || "pending"))
    : allItems;
  const name = list?.name || "Antar Surat";
  const updated = list?.updated_at
    ? new Date(list.updated_at).toLocaleString("id-ID")
    : new Date().toLocaleString("id-ID");

  // photoSize in pixels for docx (1 inch ≈ 96 px)
  const SIZE_PX = { none: 0, small: 70, medium: 120, large: 180 };
  const photoPx = SIZE_PX[photoSize] ?? 120;

  // Counts
  const counts = items.reduce((acc, it) => {
    const s = it.status || "pending";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const headerCells = ["No", "ID Pelanggan", "Nama Pelanggan", "Catatan", "Status"];
  if (photoPx > 0) headerCells.push("Foto Bukti");

  const border = { style: BorderStyle.SINGLE, size: 4, color: "BFBFBF" };
  const borders = { top: border, bottom: border, left: border, right: border };

  const headerRow = new TableRow({
    tableHeader: true,
    children: headerCells.map((t) =>
      new TableCell({
        borders,
        shading: { fill: "0F8064" },
        children: [
          new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [new TextRun({ text: t, bold: true, color: "FFFFFF" })],
          }),
        ],
      })
    ),
  });

  // Build rows (pre-resize photos)
  const rows = [headerRow];
  for (let i = 0; i < items.length; i++) {
    const it = items[i];
    const cells = [
      new TableCell({ borders, children: [new Paragraph({ alignment: AlignmentType.CENTER, text: String(i + 1) })] }),
      new TableCell({ borders, children: [new Paragraph({ children: [new TextRun({ text: it.customer_id || "", font: "Courier New" })] })] }),
      new TableCell({ borders, children: [new Paragraph(it.nama || "-")] }),
      new TableCell({ borders, children: [new Paragraph(it.note || "")] }),
      new TableCell({ borders, children: [new Paragraph({ alignment: AlignmentType.CENTER, text: fmtStatus(it) })] }),
    ];
    if (photoPx > 0) {
      let imgPara = new Paragraph({ alignment: AlignmentType.CENTER, text: "" });
      if (it.photo && typeof it.photo === "string" && it.photo.startsWith("data:image/")) {
        const r = await resizeForDocx(it.photo, photoPx, photoPx);
        if (r) {
          imgPara = new Paragraph({
            alignment: AlignmentType.CENTER,
            children: [
              new ImageRun({
                data: r.bytes,
                transformation: { width: r.width, height: r.height },
              }),
            ],
          });
        }
      }
      cells.push(new TableCell({ borders, children: [imgPara] }));
    }
    rows.push(new TableRow({ children: cells }));
  }

  const table = new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
  });

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: [
          new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [new TextRun({ text: name, bold: true, color: "0F8064" })],
          }),
          new Paragraph({
            children: [new TextRun({ text: `Total ${items.length} ID · diperbarui ${updated}`, italics: true, color: "595959" })],
          }),
          new Paragraph({
            children: [
              new TextRun({
                text:
                  `Belum: ${counts.pending || 0}   ·   ` +
                  `Sudah: ${counts.delivered || 0}   ·   ` +
                  `Tidak Ketemu: ${counts.not_found || 0}   ·   ` +
                  `Janji Bayar: ${counts.promised_pay || 0}`,
                color: "595959",
              }),
            ],
          }),
          new Paragraph({ text: "" }),
          table,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${slugify(name)}.docx`);
};

// =============== PHOTO-ONLY GRID EXPORT ===============
// Collect every photo (photos[] up to 5, fallback legacy single `photo`) across all items.
const collectPhotos = (list) => {
  const out = [];
  for (const it of list?.items || []) {
    const arr = Array.isArray(it.photos) ? it.photos.filter(Boolean).slice(0, 5) : [];
    const srcs = arr.length ? arr : (it.photo ? [it.photo] : []);
    for (const p of srcs) {
      if (typeof p === "string" && p.startsWith("data:image/")) out.push(p);
    }
  }
  return out;
};

// PDF: A4 portrait, strict 2 columns × 3 rows = 6 photos per page, more pages as needed.
// Photos only (no labels). Each photo is scaled to fit its cell preserving aspect ratio.
export const exportDeliveryPhotosPdf = async (list) => {
  const photos = collectPhotos(list);
  if (photos.length === 0) throw new Error("Tidak ada foto untuk diekspor.");
  const name = list?.name || "Antar Surat";

  const pageW = 210, pageH = 297, margin = 10, gap = 6, cols = 2, rows = 3;
  const perPage = cols * rows;
  const cellW = (pageW - 2 * margin - gap * (cols - 1)) / cols;
  const cellH = (pageH - 2 * margin - gap * (rows - 1)) / rows;

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  for (let i = 0; i < photos.length; i++) {
    const idxInPage = i % perPage;
    if (i > 0 && idxInPage === 0) doc.addPage();
    const r = Math.floor(idxInPage / cols);
    const c = idxInPage % cols;
    const cellX = margin + c * (cellW + gap);
    const cellY = margin + r * (cellH + gap);
    const resized = await resizeForPdf(photos[i], cellW, cellH);
    if (resized) {
      const x = cellX + (cellW - resized.width) / 2;
      const y = cellY + (cellH - resized.height) / 2;
      try { doc.addImage(resized.dataUrl, "JPEG", x, y, resized.width, resized.height); } catch {/* ignore */}
    }
  }

  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Halaman ${p} / ${pageCount}`, pageW - 10, pageH - 5, { align: "right" });
    doc.setTextColor(0);
  }

  doc.save(`${slugify(name)}_foto.pdf`);
};

// Word (.docx): A4 portrait, 2×3 grid per page (page break every 6), photos only.
export const exportDeliveryPhotosDocx = async (list) => {
  const photos = collectPhotos(list);
  if (photos.length === 0) throw new Error("Tidak ada foto untuk diekspor.");
  const name = list?.name || "Antar Surat";

  const cols = 2, rows = 3, perPage = cols * rows;
  // Fit 2 cols × 3 rows within A4 portrait usable area (~601px wide, ~930px tall @96dpi).
  const imgWpx = 280, imgHpx = 280;

  const resized = [];
  for (const p of photos) resized.push(await resizeForDocx(p, imgWpx, imgHpx));

  const noBorder = { style: BorderStyle.NONE, size: 0, color: "FFFFFF" };
  const borders = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

  const children = [];
  const totalPages = Math.ceil(photos.length / perPage);
  for (let page = 0; page < totalPages; page++) {
    const pageImgs = resized.slice(page * perPage, page * perPage + perPage);
    const tableRows = [];
    for (let r = 0; r < rows; r++) {
      const cells = [];
      for (let c = 0; c < cols; c++) {
        const img = pageImgs[r * cols + c];
        const para = img
          ? new Paragraph({
              alignment: AlignmentType.CENTER,
              children: [new ImageRun({ data: img.bytes, transformation: { width: img.width, height: img.height } })],
            })
          : new Paragraph({ text: "" });
        cells.push(new TableCell({
          borders,
          width: { size: 50, type: WidthType.PERCENTAGE },
          verticalAlign: VerticalAlign.CENTER,
          children: [para],
        }));
      }
      tableRows.push(new TableRow({ children: cells, cantSplit: true }));
    }
    children.push(new Table({ rows: tableRows, width: { size: 100, type: WidthType.PERCENTAGE } }));
    if (page < totalPages - 1) children.push(new Paragraph({ children: [new PageBreak()] }));
  }

  const doc = new Document({ sections: [{ properties: {}, children }] });
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `${slugify(name)}_foto.docx`);
};
